/**
 * The provider stack. Three of them, and each one is here for a reason worth stating.
 *
 *   - TanStack Query owns everything that came from the server. Defaults are deliberately
 *     unchatty: nothing in Kalqa is realtime, and a refetch-on-focus default turns one
 *     open tab per site engineer into a steady load on the API for data that changes when
 *     somebody publishes a version, which is not often.
 *   - `ContextMenuProvider` owns *one* popup for the whole application
 *     (`KLQ-20260825-006` §C.1). It is not application state and it is emphatically not a
 *     third store: it holds "which items, at which point" so that a register of 250 rows
 *     mounts one menu instead of 250. It sits at the root rather than inside the shell
 *     because a menu belongs above everything it can be opened from — including a drawer,
 *     which is the case that would otherwise clip it.
 *   - Zustand needs no provider — its stores are module-level (`lib/i18n/store.ts`). It is
 *     named here so nobody goes looking for a missing one.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { ContextMenuProvider } from '@/components/ContextMenu';
import { useLocale } from '@/lib/i18n';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Reference data (roles, presets, enums) is fetched once and shared; screen data is
        // re-read when a screen asks. Neither wants a refetch because a window regained
        // focus, so that default is off rather than tuned per query.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // A 4xx is an answer, not a hiccup. Retrying it costs the user latency and the
          // API a duplicate, and it will fail identically every time.
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });
}

export function AppProviders({ children }: { readonly children: ReactNode }) {
  // Created once per mount rather than at module scope, so a test or a future SSR pass
  // cannot leak one user's cache into another's.
  const [queryClient] = useState(createQueryClient);
  const { locale } = useLocale();

  // The document language drives hyphenation, quotation marks and the screen reader's
  // pronunciation. Leaving it at the served `en` while rendering Slovak is a real defect,
  // not a nicety.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <QueryClientProvider client={queryClient}>
      <ContextMenuProvider>{children}</ContextMenuProvider>
    </QueryClientProvider>
  );
}
